import { ReactNode, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { cn } from '@/lib/utils';
import { useOnboardingProgress, matchesOnboardingStepRoute } from '@/hooks/useOnboardingProgress';
import { OnboardingWizard } from '@/components/onboarding/OnboardingWizard';
import { useBillingBanner } from '@/App';

interface DashboardLayoutProps {
  children: ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const onboardingProgress = useOnboardingProgress();
  const hasBanner = useBillingBanner();
  const navigate = useNavigate();
  const location = useLocation();
  const hasAutoNavigatedRef = useRef(false);

  const showPanel = !onboardingProgress.loading && onboardingProgress.isActive;

  // ── Auto-navegación al reanudar onboarding ─────────────────────────────────
  // Se ejecuta aquí (DashboardLayout) para tener control explícito sobre loading.
  // El ref garantiza que solo ocurra una vez por sesión de navegación.
  useEffect(() => {
    if (hasAutoNavigatedRef.current) return;
    if (onboardingProgress.loading) return;
    // Solo auto-navegar si el usuario ya pasó el modal de bienvenida
    if (!onboardingProgress.isActive || !onboardingProgress.isStarted || !onboardingProgress.currentStep) return;

    hasAutoNavigatedRef.current = true;

    const alreadyThere = matchesOnboardingStepRoute(
      { route: onboardingProgress.currentStep.route },
      location.pathname,
      location.search,
    );

    if (!alreadyThere) {
      navigate(onboardingProgress.currentStep.route, { replace: true });
    }
  }, [onboardingProgress.loading, onboardingProgress.isActive, onboardingProgress.isStarted, onboardingProgress.currentStep?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={cn('bg-background flex flex-col', hasBanner ? 'h-[calc(100dvh-2.5rem)]' : 'h-[100dvh]')}>
      <Header />
      <main className={cn('flex-1 overflow-hidden py-2 lg:py-3 transition-[padding-right] duration-300', showPanel && 'pr-[320px]')}>
        <div className="mx-auto h-full px-3 sm:px-4 lg:px-4">
          <div className="relative h-full">
            <div className="relative h-full">{children}</div>
          </div>
        </div>
      </main>
      {!onboardingProgress.loading && (
        <OnboardingWizard progress={onboardingProgress} />
      )}
    </div>
  );
}
